import type { EditorAction, EditorState, Shape, ToolId } from './types';

export const PALETTE = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#a855f7', '#111827', '#ffffff'];

export const STROKE_WIDTHS = [2, 4, 8];

const PREFS_KEY = 'snippits.editor.prefs';

interface Prefs {
  tool: ToolId;
  color: string;
  strokeWidth: number;
}

const DEFAULT_PREFS: Prefs = {
  tool: 'rect',
  color: '#ef4444',
  strokeWidth: 4,
};

function loadPrefs(): Prefs {
  if (typeof window === 'undefined') return DEFAULT_PREFS;
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<Prefs>;
    return {
      tool: parsed.tool ?? DEFAULT_PREFS.tool,
      color: parsed.color ?? DEFAULT_PREFS.color,
      strokeWidth: parsed.strokeWidth ?? DEFAULT_PREFS.strokeWidth,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

function savePrefs(prefs: Prefs) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // localStorage may be unavailable in private mode.
  }
}

export function buildInitialEditorState(): EditorState {
  const prefs = loadPrefs();
  return {
    shapes: [],
    past: [],
    future: [],
    tool: prefs.tool,
    color: prefs.color,
    strokeWidth: prefs.strokeWidth,
    selectedId: null,
  };
}

const HISTORY_LIMIT = 100;

function pushHistory(state: EditorState): EditorState {
  const past = [...state.past, state.shapes].slice(-HISTORY_LIMIT);
  return { ...state, past, future: [] };
}

function persistFrom(state: EditorState) {
  savePrefs({ tool: state.tool, color: state.color, strokeWidth: state.strokeWidth });
}

export function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'setTool': {
      const next = { ...state, tool: action.tool, selectedId: null };
      persistFrom(next);
      return next;
    }
    case 'setColor': {
      const next = { ...state, color: action.color };
      persistFrom(next);
      return next;
    }
    case 'setStrokeWidth': {
      const next = { ...state, strokeWidth: action.width };
      persistFrom(next);
      return next;
    }
    case 'addShape':
      return { ...pushHistory(state), shapes: [...state.shapes, action.shape] };
    case 'updateShape':
      return {
        ...pushHistory(state),
        shapes: state.shapes.map((s) =>
          s.id === action.id ? ({ ...s, ...action.patch } as Shape) : s,
        ),
      };
    case 'deleteShape':
      return {
        ...pushHistory(state),
        shapes: state.shapes.filter((s) => s.id !== action.id),
        selectedId: null,
      };
    case 'select':
      return { ...state, selectedId: action.id };
    case 'undo': {
      if (state.past.length === 0) return state;
      const prev = state.past[state.past.length - 1];
      return {
        ...state,
        past: state.past.slice(0, -1),
        future: [state.shapes, ...state.future],
        shapes: prev,
        selectedId: null,
      };
    }
    case 'redo': {
      if (state.future.length === 0) return state;
      const next = state.future[0];
      return {
        ...state,
        past: [...state.past, state.shapes],
        future: state.future.slice(1),
        shapes: next,
        selectedId: null,
      };
    }
    default:
      return state;
  }
}
